import { AxiosResponse } from 'axios';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { AnalyticsApi } from '../../services';
import {
  AnalyticsCountryUsersItem,
  AnalyticsDimensionActivityItem,
  AnalyticsPeriod,
  AnalyticsReportUsageItem,
  AnalyticsSummary,
  AnalyticsSummaryApiItem,
  AnalyticsUserActivityItem,
} from '../../services/AnalyticsApi';
import { HttpRequestError } from '../../types';
import { analyticsPeriodSelector } from '../selectors';
import {
  findAnalyticsCountriesSuccess,
  findAnalyticsCountriesUsersSuccess,
  findAnalyticsCountryReportsSuccess,
  findAnalyticsCountryRolesSuccess,
  findAnalyticsFailure,
  findAnalyticsRolesSuccess,
  findAnalyticsRoleReportsSuccess,
  findAnalyticsSummarySuccess,
  findAnalyticsTopSuccess,
  findAnalyticsUsageSuccess,
  findAnalyticsUsersSuccess,
} from './actionCreators';
import { ActionType, FindAnalyticsRequestedAction } from './types';

function* findAnalytics({ period }: FindAnalyticsRequestedAction) {
  try {
    const toNumber = (value: string | number | undefined) => Number(value || 0);
    const normalizeMetrics = (metrics?: {
      executions?: string | number;
      exports?: string | number;
      errors?: string | number;
    }) => ({
      executions: toNumber(metrics?.executions),
      exports: toNumber(metrics?.exports),
      errors: toNumber(metrics?.errors),
    });

    const normalizeUsageItems = (items: AnalyticsReportUsageItem[]) =>
      items.map(item => ({
        ...item,
        metrics: normalizeMetrics(item.metrics),
      }));

    const normalizeUserItems = (items: AnalyticsUserActivityItem[]) =>
      items.map(item => ({
        ...item,
        metrics: normalizeMetrics(item.metrics),
      }));

    const normalizeDimensionItems = (items: AnalyticsDimensionActivityItem[]) =>
      items.map(item => ({
        ...item,
        metrics: normalizeMetrics(item.metrics),
      }));

    const normalizeCountryUsersItems = (items: AnalyticsCountryUsersItem[]) =>
      items.map(item => ({
        ...item,
        users: toNumber(
          item.users ||
            item.total_users ||
            item.metrics?.users ||
            item.metrics?.total_users ||
            item.metrics?.unique_users
        ),
      }));

    const selectedPeriod: AnalyticsPeriod = period || (yield select(analyticsPeriodSelector));

    const defaultSummary: AnalyticsSummary = {
      executions: 0,
      exports: 0,
      errors: 0,
      users: 0,
      countries: 0,
      roles: 0,
    };

    const loadSummary = function* loadSummary() {
      try {
        const { data: summary }: AxiosResponse<AnalyticsSummaryApiItem[]> = yield call(
          AnalyticsApi.findSummary,
          selectedPeriod
        );
        const summaryItem = Array.isArray(summary) ? summary[0] : undefined;
        const metrics = summaryItem?.metrics;

        const normalizedSummary: AnalyticsSummary = {
          executions: toNumber(metrics?.total_executions),
          exports: toNumber(metrics?.total_exports),
          errors: toNumber(metrics?.errors),
          users: toNumber(metrics?.unique_users),
          countries: toNumber(metrics?.unique_country),
          roles: toNumber(metrics?.unique_roles),
        };

        yield put(findAnalyticsSummarySuccess(normalizedSummary));
      } catch (error) {
        yield put(findAnalyticsSummarySuccess(defaultSummary));
      }
    };

    const loadUsage = function* loadUsage() {
      try {
        const { data: usage }: AxiosResponse<AnalyticsReportUsageItem[]> = yield call(
          AnalyticsApi.findReportsUsage,
          selectedPeriod
        );
        yield put(findAnalyticsUsageSuccess(normalizeUsageItems(usage)));
      } catch (error) {
        yield put(findAnalyticsUsageSuccess([]));
      }
    };

    const loadTop = function* loadTop() {
      try {
        const { data: top }: AxiosResponse<AnalyticsReportUsageItem[]> = yield call(
          AnalyticsApi.findReportsTop,
          selectedPeriod
        );
        yield put(findAnalyticsTopSuccess(normalizeUsageItems(top)));
      } catch (error) {
        yield put(findAnalyticsTopSuccess([]));
      }
    };

    const loadUsers = function* loadUsers() {
      try {
        const { data: users }: AxiosResponse<AnalyticsUserActivityItem[]> = yield call(
          AnalyticsApi.findUsersActivity,
          selectedPeriod
        );
        yield put(findAnalyticsUsersSuccess(normalizeUserItems(users)));
      } catch (error) {
        yield put(findAnalyticsUsersSuccess([]));
      }
    };

    const loadCountries = function* loadCountries() {
      try {
        const { data: countries }: AxiosResponse<AnalyticsDimensionActivityItem[]> = yield call(
          AnalyticsApi.findCountriesActivity,
          selectedPeriod
        );
        yield put(findAnalyticsCountriesSuccess(normalizeDimensionItems(countries)));
      } catch (error) {
        yield put(findAnalyticsCountriesSuccess([]));
      }
    };

    const loadRoles = function* loadRoles() {
      try {
        const { data: roles }: AxiosResponse<AnalyticsDimensionActivityItem[]> = yield call(
          AnalyticsApi.findRolesActivity,
          selectedPeriod
        );
        yield put(findAnalyticsRolesSuccess(normalizeDimensionItems(roles)));
      } catch (error) {
        yield put(findAnalyticsRolesSuccess([]));
      }
    };

    const loadCountriesUsers = function* loadCountriesUsers() {
      try {
        const { data: countriesUsers }: AxiosResponse<AnalyticsCountryUsersItem[]> = yield call(
          AnalyticsApi.findCountriesUsers,
          selectedPeriod
        );
        yield put(findAnalyticsCountriesUsersSuccess(normalizeCountryUsersItems(countriesUsers)));
      } catch (error) {
        yield put(findAnalyticsCountriesUsersSuccess([]));
      }
    };

    const loadCountryReports = function* loadCountryReports() {
      try {
        const { data: countryReports }: AxiosResponse<AnalyticsDimensionActivityItem[]> =
          yield call(AnalyticsApi.findCountryReportsActivity, selectedPeriod);
        yield put(findAnalyticsCountryReportsSuccess(normalizeDimensionItems(countryReports)));
      } catch (error) {
        yield put(findAnalyticsCountryReportsSuccess([]));
      }
    };

    const loadRoleReports = function* loadRoleReports() {
      try {
        const { data: roleReports }: AxiosResponse<AnalyticsDimensionActivityItem[]> = yield call(
          AnalyticsApi.findRoleReportsActivity,
          selectedPeriod
        );
        yield put(findAnalyticsRoleReportsSuccess(normalizeDimensionItems(roleReports)));
      } catch (error) {
        yield put(findAnalyticsRoleReportsSuccess([]));
      }
    };

    const loadCountryRoles = function* loadCountryRoles() {
      try {
        const { data: countryRoles }: AxiosResponse<AnalyticsDimensionActivityItem[]> = yield call(
          AnalyticsApi.findCountryRolesActivity,
          selectedPeriod
        );
        yield put(findAnalyticsCountryRolesSuccess(normalizeDimensionItems(countryRoles)));
      } catch (error) {
        yield put(findAnalyticsCountryRolesSuccess([]));
      }
    };

    yield all([
      call(loadSummary),
      call(loadUsage),
      call(loadTop),
      call(loadUsers),
      call(loadCountries),
      call(loadRoles),
      call(loadCountriesUsers),
      call(loadCountryReports),
      call(loadRoleReports),
      call(loadCountryRoles),
    ]);
  } catch (error) {
    yield put(findAnalyticsFailure(error as HttpRequestError));
  }
}

export default [takeLatest(ActionType.findAnalyticsRequested, findAnalytics)];
